import { memo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import {
  Button,
  Layout,
  PageTitle,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { DiscardCardIcon, SupportStrokeIcon } from "../assets/icons";
import { APP_ROUTES } from "../constants";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useClaimLink } from "../hooks/useClaimLink";
import { useClaimableGuard } from "../hooks/useClaimableGuard";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { getEvents, safeCapture } from "../analytics";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { usablePersonName } from "../utils/homeGreeting";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from "../components/Navbar";

/**
 * Class A interstitial after "Discard" on the payout-claim intention.
 * Regresar → intention (no Auth). Reject is not persisted.
 */
export const IntentionDiscardPage = memo(() => {
  useClaimLink();
  useClaimableGuard();
  const { t } = useTranslation();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { redirectTo } = useRouteWithToken();

  const colorConfig = theme.errorView;
  const {
    backgroundBtn,
    textColorBtn,
    title,
    bodyCopy,
    iconColors,
    backgroundIcon,
  } = colorConfig;

  const senderName =
    usablePersonName(session.data?.config?.senderName as string | undefined) ||
    t("payoutClaim.someone");

  useEffect(() => {
    safeCapture(getEvents().INTENTION_DISCARD_VIEW);
  }, []);

  const handleGoBack = () => {
    safeCapture(getEvents().INTENTION_DISCARD_GO_BACK_CLICK);
    redirectTo(APP_ROUTES.CLAIM_ENTRY_PAGE);
  };

  const { openSupport, supportSheet } = useSupportSheet();

  return (
    <>
      <AuraLayout colorConfig={colorConfig} className="need-help-header flow-landing-page">
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            noBackButton
            color={title}
            onIconClick={() => openSupport()}
            icon={
              <SupportStrokeIcon
                size={35}
                colors={[backgroundIcon, backgroundIcon]}
                glyphColor={iconColors[0]}
              />
            }
          />
        </Layout.Header>
        <Layout.Content className="landing-section flow-hero-section flow-hero-section--with-header">
          <ScrollFadeIndicator color={colorConfig.bodyCopy} />
          <section className="flow-hero">
            <FlowHeroMark backgroundIcon={backgroundIcon}>
              <DiscardCardIcon size={60} colors={iconColors} />
            </FlowHeroMark>
            <section className="landing-container">
              <PageTitle
                highlight={t("payoutClaim.discardScreen.highlight")}
                highlightColor={title}
                secudnary={t("payoutClaim.discardScreen.secondary")}
                secudnaryColor={title}
              />
              <BodyCopy textColor={bodyCopy}>
                {t("payoutClaim.discardScreen.body", { name: senderName })}
              </BodyCopy>
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              onClick={handleGoBack}
            >
              {t("payoutClaim.discardScreen.cta")}
            </Button>
          </section>
        </Layout.Footer>
      </AuraLayout>
      {supportSheet}
    </>
  );
});
